import { ProcessPathState, ValidationErrors } from 'src/pages/settings/tabs/siteLayout/types';

/**
 * A valid line name must be the exact word "Line" (case-sensitive) followed by
 * a single space and either:
 *   - a two-digit number from 01 to 99 (e.g. "Line 01", "Line 42", "Line 99"), or
 *   - a single uppercase letter A-Z (e.g. "Line A", "Line Z").
 *
 * The word "Line" is case-sensitive, so "line 05" and "LINE 05" are rejected.
 */
export const LINE_NAME_PATTERN = /^Line (0[1-9]|[1-9][0-9]|[A-Z])$/;

/** Human-readable description of the expected line-name format. */
export const LINE_NAME_FORMAT_MESSAGE = 'Line name must be in the format "Line 01" to "Line 99" or "Line A" to "Line Z"';

/**
 * Returns a validation error message for a line name, or undefined if valid.
 * Shared by the inline input validation and the save-gating validation so the
 * two stay consistent.
 */
export function getLineNameError(lineName: string | undefined | null): string | undefined {
  if (!lineName || lineName.trim() === '') {
    return 'Line name is required';
  }
  if (!LINE_NAME_PATTERN.test(lineName.trim())) {
    return LINE_NAME_FORMAT_MESSAGE;
  }
  return undefined;
}

/**
 * Validates the entire SiteLayout state before save.
 * Returns an object mapping field paths to error messages.
 * Returns empty object if all validations pass.
 */
export function validateSiteLayout(processPaths: ProcessPathState[]): ValidationErrors {
  const errors: ValidationErrors = {};

  // Check for duplicate process paths. A process path is uniquely identified by
  // its processId + functionId, so multiple functions may share a processId.
  const processPathKeys = new Set<string>();

  for (let ppIdx = 0; ppIdx < processPaths.length; ppIdx++) {
    const pp = processPaths[ppIdx];
    const ppKey = `processPaths[${ppIdx}]`;
    const uniqueKey = `${pp.processId}-${pp.functionId}`;

    // Duplicate process path check
    if (processPathKeys.has(uniqueKey)) {
      errors[`${ppKey}.processId`] = 'Duplicate process path';
    }
    processPathKeys.add(uniqueKey);

    if (pp.staffingMode === 'PROCESS_LINES') {
      // Validate lines
      for (let lineIdx = 0; lineIdx < pp.lines.length; lineIdx++) {
        const line = pp.lines[lineIdx];
        const lineKey = `${ppKey}.lines[${lineIdx}]`;

        const lineNameError = getLineNameError(line.lineName);
        if (lineNameError) {
          errors[`${lineKey}.lineName`] = lineNameError;
        }

        // Validate workstation IDs are non-empty
        for (let wsIdx = 0; wsIdx < line.workstations.length; wsIdx++) {
          if (!line.workstations[wsIdx].workstationId || line.workstations[wsIdx].workstationId.trim() === '') {
            errors[`${lineKey}.workstations[${wsIdx}].workstationId`] = 'Workstation ID is required';
          }
        }
      }
    } else if (pp.staffingMode === 'DEPARTMENT') {
      // Validate individual workstation IDs are non-empty
      for (let wsIdx = 0; wsIdx < pp.individualWorkstations.length; wsIdx++) {
        if (!pp.individualWorkstations[wsIdx].workstationId || pp.individualWorkstations[wsIdx].workstationId.trim() === '') {
          errors[`${ppKey}.individualWorkstations[${wsIdx}].workstationId`] = 'Workstation ID is required';
        }
      }
    }
  }

  return errors;
}
